import { csvParse } from "d3-dsv";
import { format } from "d3-format";
import { formatNumber, FORMATTERS } from "./text.js";
import { urbanColors } from "@urbaninstitute/dataviz-components/utils";

/**
 * @typedef {Object} ColumnConfig
 * @property {string} column_name
 * @property {string} column_label
 * @property {string} data_formatting
 * @property {string} scale_color @property {string} scale_type
 * @property {string} scale_breaks
 * @property {string} scale_min_max
 * @property {(d: number) => string} format
 */

const BASE_CONFIG = {
  map: {
    stroke: urbanColors.white,
    hoverStroke: urbanColors.black,
    strokeWidth: 1,
    hoverStrokeWidth: 3,
    fontColor: urbanColors.gray_shade_darkest,
    fontWeight: "bold",
    fontSize: 18
  }
};

export class ToolData {
  specialColumns = ["map_id", "map_description"];
  /** @type {Set<string>} */
  dataColumns = new Set();
  /** @type {Set<string>} */
  tableColumns = new Set();
  /** @param {string} rawData */
  constructor(rawData, rawTableData, rawConfigData) {
    this.config = this.parseConfig(rawConfigData);
    this.data = this.parseData(rawData);
    for (let col in this.data[0]) {
      if (!this.specialColumns.includes(col)) {
        this.dataColumns.add(col);
      }
    }

    if (rawTableData) {
      this.setTableData(rawTableData);
    } else {
      this.setTableData(this.data);
    }
  }

  /**
   * @param {Array<Record<string, any>>} rawData
   * @returns {Record<string, (number | string)>[]}
   */
  parseData(rawData) {
    return rawData.map((d) => {
      /** @type {Record<string, (number | string)>} */
      let result = {};
      for (const k of Object.keys(d)) {
        if (!this.specialColumns.includes(k)) {
          // try to convert value to number
          result[k] = Number(d[k]);
          // if number conversion fails, keep as string
          if (isNaN(result[k])) {
            result[k] = d[k];
          }
        } else {
          result[k] = d[k];
        }
      }
      return result;
    });
  }

  /** @param {Array<Record<string, any>>} rawData */
  setTableData(rawData, options = {}) {
    const defaultOptions = {
      stateSummaryLabel: "Statewide",
    };

    this.tableDataOptions = Object.assign(defaultOptions, options);
    this.tableData = this.parseData(rawData);
    let dataKeys = Object.keys(this.tableData[0]);
    for (let i = 0; i < dataKeys.length; i++) {
      const col = dataKeys[i];
      if (i == 1) {
        this.labelColumn = col;
      }
      if (!this.specialColumns.includes(col)) {
        this.tableColumns.add(col);
      }
    }
  }

  getData() {
    return this.data;
  }

  getTableData() {
    return this.tableData;
  }

  getStateTableData(mapId, includeStateSummary = false) {
    let allData = this.getTableData();
    let result = allData.filter((row) => row.map_id === mapId);
    let stateData = this.getData().find((row) => row.map_id === mapId);
    if (includeStateSummary && this.labelColumn && stateData) {
      let tableStateSummary = Object.assign({}, stateData);
      tableStateSummary[this.labelColumn] = this.tableDataOptions.stateSummaryLabel;
      return [tableStateSummary].concat(result);
    }
    return result;
  }
  getStateData(mapId, stateSummaryData) {
    let result = this.data.filter((row) => row.map_id === mapId);
    if (stateSummaryData && this.labelColumn) {
      // make sure keys match the table data
      let tableStateSummary = Object.assign({}, stateSummaryData);
      tableStateSummary[this.labelColumn] = this.options.stateSummaryLabel;
      return [tableStateSummary].concat(result);
    }
    return result;
  }

  getDataString() {
    return JSON.stringify(this.data);
  }

  getDataColumns() {
    return Array.from(this.dataColumns);
  }
  /**
   * @param {string} map_id
   * @returns {Record<string, (number | string)>}
   */
  getRow(map_id) {
    return this.data.find((row) => row.map_id === map_id) || this.getFallback(map_id);
  }

  /**
   * @param {string} map_id
   * @returns {Record<string, (number | string)>}
   */
  getFallback(map_id) {
    let fallbackData = { map_id: map_id };
    this.getDataColumns().forEach((col) => {
      fallbackData[col] = "N/A";
    });
    return fallbackData;
  }

  toTableData() {
    const rawData = this.getData();
    const tableData = new TableData(rawData, { showStateSummary: false, fromJson: true });
    return tableData;
  }

  /**
   * @param {ColumnConfig[]} columnConfig
   * @param {string} accentColor
   */
  parseConfig(columnConfig, accentColor) {
    let columnMap = new Map();
    for (let col of columnConfig) {
      columnMap.set(col.column_name, this.parseColumnConfig(col));
    }
    let combinedConfig = Object.assign(BASE_CONFIG, { columns: columnMap });
    if (accentColor) {
      combinedConfig.map.hoverStroke = accentColor;
    }
    return combinedConfig;
  }

  /**
   * @param {ColumnConfig} columnConfig
   */
  parseColumnConfig(columnConfig) {
    let result = {
      column_name: columnConfig.column_name,
      column_label: columnConfig.column_label.trim()
        ? columnConfig.column_label
        : columnConfig.column_name,
      data_formatting: columnConfig.data_formatting,
      scale_color: columnConfig.scale_color.trim() === "" ? "blue" : columnConfig.scale_color,
      scale_type: columnConfig.scale_type === "" ? "linear" : columnConfig.scale_type,
      scale_breaks:
        columnConfig.scale_breaks.trim() === ""
          ? undefined
          : parseArrayStr(columnConfig.scale_breaks),
      scale_min_max:
        columnConfig.scale_min_max.trim() === ""
          ? undefined
          : parseArrayStr(columnConfig.scale_min_max)
    };
    let defaultFormats = Object.keys(FORMATTERS);
    if (defaultFormats.includes(result.data_formatting)) {
      result.format = (d) => formatNumber(d, result.data_formatting);
    } else {
      result.format = (d) => format(result.data_formatting)(d) || "N/A";
    }
    return result;
  }
  getTableColumns() {
    return Array.from(this.tableColumns);
  }
}

/**
 *
 * @param {string} rawCSV
 * @param {string[]} specialColumns
 * @returns {Record<string, (number | string)>[]}
 */
function parseCSVString(rawCSV, specialColumns) {
  return csvParse(rawCSV).map((d) => {
    /** @type {Record<string, (number | string)>} */
    let result = {};
    for (const k of Object.keys(d)) {
      if (!specialColumns.includes(k)) {
        // try to convert value to number
        result[k] = Number(d[k]);
        // if number conversion fails, keep as string
        if (isNaN(result[k])) {
          result[k] = d[k];
        }
      } else {
        result[k] = d[k];
      }
    }
    return result;
  });
}

/**
 * @param {string | undefined} arrayStr
 * @returns {number[] | undefined}
 */
function parseArrayStr(arrayStr) {
  return arrayStr ? arrayStr.split(",").map((str) => Number(str.trim())) : undefined;
}
